import React, { useState, useEffect, useMemo } from 'react';
import { Image as ImageIcon, Search, Download, ExternalLink, X } from 'lucide-react';
import { listCreatives } from '../../utils/adminApi';

const TEAL = '#2EB4AC';
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CreativeLibraryView = ({ showToast }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [previewing, setPreviewing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listCreatives()
      .then((r) => { if (!cancelled) setItems(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) showToast?.('Failed to load creative library', 'error'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [showToast]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((c) =>
      !q || c.name.toLowerCase().includes(q) || (c.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  }, [items, search]);

  const download = async (creative) => {
    try {
      const response = await fetch(`${API_BASE}${creative.url}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = creative.filename || `${creative.name}.${creative.ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast?.('Download started', 'success');
    } catch {
      showToast?.('Failed to download', 'error');
    }
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="creative-library-view">
      <div className="flex items-center gap-2 mb-4">
        <ImageIcon size={20} style={{ color: TEAL }} />
        <h2 className="text-base font-semibold text-slate-700">Browse campaign creatives</h2>
        <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">read only</span>
      </div>

      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or tag..."
          data-testid="creative-library-search"
          className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      </div>

      {loading ? (
        <p className="text-xs text-slate-400 text-center py-12">Loading creatives...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-xl">
          <ImageIcon size={28} className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm font-semibold text-slate-700">{items.length === 0 ? 'Library is empty' : 'No matches'}</p>
          <p className="text-[11px] text-slate-500 mt-1">{items.length === 0 ? 'Admins can upload creatives from the Admin panel.' : 'Try a different search term.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="creative-library-grid">
          {filtered.map((c) => (
            <div key={c.id} className="bg-white border border-slate-200 hover:border-teal-300 hover:shadow-md rounded-xl overflow-hidden flex flex-col group transition-all"
              data-testid={`creative-library-card-${c.name}`}>
              <button
                onClick={() => setPreviewing(c)}
                className="aspect-square bg-slate-50 flex items-center justify-center relative">
                <img src={`${API_BASE}${c.url}`} alt={c.name} className="w-full h-full object-contain p-2" loading="lazy" />
                <span className="absolute top-1.5 right-1.5 text-[9px] font-bold uppercase bg-white/90 text-slate-700 px-1.5 py-0.5 rounded">{c.ext}</span>
              </button>
              <div className="p-2 flex-1 flex flex-col">
                <div className="text-[11px] font-bold text-slate-900 truncate" title={c.name}>{c.name}</div>
                <div className="text-[9px] text-slate-400 mt-0.5">
                  {new Date(c.created_at).toLocaleDateString()} · {((c.size_bytes || 0) / 1024).toFixed(1)} KB
                </div>
                {(c.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-0.5 mt-1">
                    {c.tags.slice(0, 2).map((t) => (
                      <span key={t} className="text-[9px] px-1 py-0.5 bg-teal-50 text-teal-700 rounded-full">#{t}</span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-1 mt-2">
                  <button
                    onClick={() => setPreviewing(c)}
                    title="View full"
                    data-testid={`creative-library-view-${c.name}`}
                    className="flex-1 flex items-center justify-center gap-1 text-[10px] font-semibold py-1 rounded border border-slate-200 text-slate-700 hover:bg-slate-50">
                    <ExternalLink size={10} /> View
                  </button>
                  <button
                    onClick={() => download(c)}
                    title="Download"
                    data-testid={`creative-library-download-${c.name}`}
                    className="flex-1 flex items-center justify-center gap-1 text-[10px] font-semibold py-1 rounded text-white hover:opacity-90"
                    style={{ backgroundColor: TEAL }}>
                    <Download size={10} /> Save
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {previewing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPreviewing(null)}
          data-testid="creative-library-preview-modal">
          <button className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white" onClick={() => setPreviewing(null)}>
            <X size={20} />
          </button>
          <div className="flex flex-col items-center gap-3 max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img src={`${API_BASE}${previewing.url}`} alt={previewing.name}
              className="max-w-full max-h-[78vh] object-contain rounded-lg shadow-2xl" />
            <div className="text-white text-center">
              <div className="text-sm font-semibold">{previewing.name}</div>
              <div className="text-[11px] opacity-70 mt-0.5">
                {previewing.ext?.toUpperCase()} · {((previewing.size_bytes || 0) / 1024).toFixed(1)} KB · uploaded {new Date(previewing.created_at).toLocaleString()}
              </div>
              {previewing.notes && (
                <p className="text-[11px] opacity-80 mt-2 max-w-md text-center italic">{previewing.notes}</p>
              )}
            </div>
            <button onClick={() => download(previewing)}
              className="flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white rounded-lg hover:opacity-90"
              style={{ backgroundColor: TEAL }}>
              <Download size={13} /> Download image
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreativeLibraryView;
